import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

export function PinDialog({ open, onOpenChange, pinSet, onSaved }) {
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPin2, setNewPin2] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  useEffect(() => {
    if (open) { setCurrentPin(""); setNewPin(""); setNewPin2(""); }
  }, [open]);

  const save = async () => {
    if (newPin.length < 8) { toast.error("PIN must be at least 8 characters."); return; }
    if (newPin !== newPin2) { toast.error("PINs do not match."); return; }
    setSaving(true);
    try {
      await api.post("/admin/security/pin", { new_pin: newPin, current_pin: currentPin || undefined });
      toast.success(pinSet ? "Delete PIN updated" : "Delete PIN set");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save PIN");
    } finally {
      setSaving(false);
    }
  };

  const forgotPin = async () => {
    setSendingReset(true);
    try {
      const res = await api.post("/admin/security/pin/reset-request");
      toast.success(`Reset link sent to ${res.data.sent_to}. It expires in 15 minutes.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reset email");
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Lock size={18} className="text-amber-400" /> {pinSet ? "Change delete PIN" : "Set delete PIN"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            This PIN is required to delete any account. Keep it private. It protects your users even if your login is compromised. Minimum 8 characters.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {pinSet && (
            <Input
              data-testid="pin-current"
              type="password"
              value={currentPin}
              onChange={(e) => setCurrentPin(e.target.value)}
              placeholder="Current PIN"
              className="bg-black border-zinc-800 focus-visible:ring-blue-600"
            />
          )}
          <Input
            data-testid="pin-new"
            type="password"
            value={newPin}
            onChange={(e) => setNewPin(e.target.value)}
            placeholder="New PIN (min 8 characters)"
            className="bg-black border-zinc-800 focus-visible:ring-blue-600"
          />
          <Input
            data-testid="pin-new-confirm"
            type="password"
            value={newPin2}
            onChange={(e) => setNewPin2(e.target.value)}
            placeholder="Confirm new PIN"
            className="bg-black border-zinc-800 focus-visible:ring-blue-600"
          />
          {pinSet && (
            <button
              type="button"
              data-testid="pin-forgot"
              onClick={forgotPin}
              disabled={sendingReset}
              className="text-xs text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
            >
              {sendingReset ? "Sending reset link…" : "Forgot your PIN? Email me a reset link"}
            </button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="bg-transparent border-white/10 hover:bg-zinc-800" data-testid="pin-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="pin-save">
            {saving ? "Saving…" : (pinSet ? "Update PIN" : "Set PIN")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
